// Imports de librerías
import { Group, Raycaster, Vector2 } from 'three';
import gsap, { Power2, Power3 } from 'gsap';
import BezierEasing from 'bezier-easing';

// Imports internos
import WebGLSketch from '../_app/cuchillo/3D/WebGLSketch';
import Image from '../_app/cuchillo/3D/Image';
import InfiniteScroll from '../_app/cuchillo/scroll/InfiniteScroll';
import InfiniteScroll__Item from '../_app/cuchillo/scroll/InfiniteScroll__Item';
import { Metrics } from '../_app/cuchillo/core/Metrics';
import { GetBy } from '../_app/cuchillo/core/Element';
import { Interaction } from '../_app/cuchillo/core/Interaction';
import { Maths } from '../_app/cuchillo/utils/Maths';
import { Functions } from '../_app/cuchillo/utils/Functions';
import { CSS } from '../_app/cuchillo/utils/CSS';
import { isMobile, isSmartphone } from '../_app/cuchillo/core/Basics';
import { Sizes } from '../_app/cuchillo/core/Sizes';


export default class CircleSlider extends Group {
    static instance = null;
    static _alpha = 1;
    static data = {};
    static isModeSuperman = false;
    static easing = BezierEasing(0.69, 0.27, 0.43, 0.56);
    static easingX = BezierEasing(0.85, 0.45, 0.84, 0.54);

    static get alpha() {
        return this._alpha;
    }

    static set alpha(value) {
        this._alpha = value;
        const instance = this.getInstance();
        if (instance?.scroller) {
            instance.scroller._items.forEach(item => {
                item.image.material.uniforms.opacity.value = value;
            });
        }
    }

    static getInstance(dom) {
        if (!this.instance) {
            this.instance = new CircleSlider(dom);
        }
        return this.instance;
    }


    constructor(dom) {
        if (CircleSlider.instance) return CircleSlider.instance;
        super();

        this.dom = dom;
        this.link = new CircleSlider__Link();
        this.meshes = [];
        this.total = 0;
        this.actual = null;
        this.isShow = false;
        this.visible = false;
        this.raycaster = new Raycaster();
        this.pointer = new Vector2();
        this.src404 = this.dom.dataset.src;

        CircleSlider.instance = this;

        this.load();
        this.setupRaycaster();
    }

    modeSuperman() {
        CircleSlider.isModeSuperman = true;
        this.scroller.minSpeed = -2;

        this.scroller._items.map(item => {
            if (item.indice % 2 != 0) {
                item.image.visible = false;
                item.isBlocked = true;
            }
        });

        this.link.show("404 - Phantom Zone", "/", true);
    }

    load() {
        this.scroller = new InfiniteScroll(InfiniteScroll.AXIS_X, {
            domResize: this.dom,
            container: this.dom,
            multiplicator: 1,
            itemClass: CircleSlider__Item,
            minSpeed: 2,
            inverted: false,
            gap: 0,
            hasVirtuaScroll: true,
        });
    }

    setupRaycaster() {
        this.meshes = this.scroller._items
            .map(item => item?.image?.mesh)
            .filter(Boolean);
    }

    show() {
        gsap.killTweensOf(CircleSlider);

        this.scroller.start();
        this.scroller.show();

        this.visible = true;
        this.isShow = true;
        this.alpha = 0;

        if (!CircleSlider.isModeSuperman) {
            this.scroller.minSpeed = Metrics.WIDTH * .15;
            gsap.to(this.scroller, { minSpeed: 2, ease: Power2.easeOut, duration: 1.5 });
        }

        gsap.to(CircleSlider, { alpha: 1, ease: Power2.easeIn, duration: 0.1 });
    }

    hide() {
        this.isShow = false;
        gsap.killTweensOf(CircleSlider);

        gsap.to(this.scroller, { minSpeed: 400, ease: Power3.easeIn, duration: 1 });
        gsap.to(CircleSlider, {
            alpha: 0,
            ease: Power2.easeIn,
            duration: 0.3,
            delay: 0.8,
            onComplete: () => {
                this.visible = false;
                this.scroller.hide();
            },
        });

        this.link.hide();
    }

    checkHover() {
        if (!this.isShow) return;

        const mouse = Interaction.positions.mouse;
        this.pointer.set(
            (mouse.x / window.innerWidth) * 2 - 1,
            -(mouse.y / window.innerHeight) * 2 + 1
        );

        this.raycaster.setFromCamera(this.pointer, WebGLSketch.camera);
        const intersects = this.raycaster.intersectObjects(this.meshes, false);

        if (intersects.length > 0) {
            const uuid = intersects[0].object.uuid;
            if (this.actual !== uuid) {
                this.actual = uuid;
                const { title, url, isShow } = CircleSlider.data[uuid];
                if (isShow) this.link.show(title, url);
            }
        } else {
            this.link.hide();
            this.actual = null;
        }
    }

    loop() {
        if (!this.scroller.enabled) return;
        this.scroller.loop();
        this.link.loop();
        if (CircleSlider.isModeSuperman) return;
        this.checkHover();

    }

    resize() {
        this.scroller.resize();
    }
}



class CircleSlider__Link {
    container;
    link;
    isShow = false;
    text = ""

    constructor() {
        this.container = GetBy.id("link-visor");
        this.link = GetBy.selector("a", this.container)[0];
    }

    show(text, url, isInternal = false) {
        this.link.textContent = text;
        this.link.href = url;
        this.container.style.pointerEvents = "all";

        if (this.isShow) return;

        this.isShow = true;
        this.container.style.visibility = "visible";

        if (isInternal) {
            this.link.setAttribute("data-internal", "true")
        }

    }

    hide() {
        if (!this.isShow) return;

        this.isShow = false;
        this.container.style.visibility = "hidden";
    }

    loop() {
        this.container.style[CSS.transform] = CSS.translate3D(Interaction.positions.mouse.x, Interaction.positions.mouse.y, 999);
    }

    dispose() {

    }
}


class CircleSlider__Item extends InfiniteScroll__Item {
    group = new Group();
    domImage;
    isReset = false;
    isShow = false;
    isBlocked = false;
    image;
    title;
    url;
    refWidth;
    offset = {}
    offsetPositionY = .55;
    modAspectRatio = 1;
    modSpeed = 1.859;

    constructor(__d, __i, __axis = InfiniteScroll.AXIS_Y) {
        super(__d, __i, __axis, false);
        this.domImage = GetBy.selector("span", this.dom)[0];
        this.image = new Image({ dom: this.domImage, hasFreePosition: true });
        this.title = this.domImage.dataset.title;
        this.url = this.domImage.dataset.url;
        this.group.add(this.image);

        CircleSlider.getInstance().add(this.group);
        CircleSlider.data[this.image.mesh.uuid] = this;

        this.image.visible = false;

        this.resize();
        this.reset();
    }

    reset() {
        if (this.isReset) return;
        this.isReset = true;
        if (CircleSlider.isModeSuperman) {
            this.offset = {
                y: Maths.maxminRandom(200, 50),
                x: Maths.maxminRandom(50, 5),


                // y: Maths.maxminRandom(Metrics.parseSize("180fpx"), Metrics.parseSize("44fpx")),
                // x: Maths.maxminRandom(Metrics.WIDTH * .05, Metrics.WIDTH * .01),

                //y: Maths.maxminRandom(Metrics.parseSize("180fpx"), Metrics.parseSize("44fpx")),
                //x: Maths.maxminRandom(Metrics.parseSize("40fpx"), Metrics.parseSize("3fpx")),
                rotation: {
                    y0: Maths.maxminRandom(54, 24),
                    x0: Maths.maxminRandom(54, 24),
                    z0: Maths.maxminRandom(12, 6),
                    y1: Maths.maxminRandom(24, 10),
                    x1: Maths.maxminRandom(24, 10),
                    z1: Maths.maxminRandom(12, 2),
                    yMod: Math.random() > .5 ? 1 : -1,
                    xMod: Math.random() > .5 ? -1 : 1,
                    zMod: Math.random() > .5 ? -1 : 1,
                }
            }
        } else {
            this.offset = {
                y: Maths.maxminRandom(200, 50),
                x: Maths.maxminRandom(50, 5),


                // y: Maths.maxminRandom(Metrics.parseSize("180fpx"), Metrics.parseSize("44fpx")),
                // x: Maths.maxminRandom(Metrics.WIDTH * .05, Metrics.WIDTH * .01),

                //y: Maths.maxminRandom(Metrics.parseSize("180fpx"), Metrics.parseSize("44fpx")),
                //x: Maths.maxminRandom(Metrics.parseSize("40fpx"), Metrics.parseSize("3fpx")),
                rotation: {
                    y0: Maths.maxminRandom(12, 4),
                    x0: Maths.maxminRandom(12, 4),
                    z0: Maths.maxminRandom(4, 2),
                    y1: Maths.maxminRandom(12, 4),
                    x1: Maths.maxminRandom(12, 4),
                    z1: Maths.maxminRandom(4, 2),
                    yMod: Math.random() > .5 ? 1 : -1,
                    xMod: Math.random() > .5 ? -1 : 1,
                    zMod: Math.random() > .5 ? -1 : 1,
                }
            }
        }

        if (Metrics.WIDTH < Sizes.SMARTPHONE) {

            this.offset = {
                y: Maths.maxminRandom(200, 20) * -1,
                x: Maths.maxminRandom(50, 5),


                // y: Maths.maxminRandom(Metrics.parseSize("180fpx"), Metrics.parseSize("44fpx")),
                // x: Maths.maxminRandom(Metrics.WIDTH * .05, Metrics.WIDTH * .01),

                //y: Maths.maxminRandom(Metrics.parseSize("180fpx"), Metrics.parseSize("44fpx")),
                //x: Maths.maxminRandom(Metrics.parseSize("40fpx"), Metrics.parseSize("3fpx")),

                rotation: {
                    y0: Maths.maxminRandom(12, 4),
                    x0: Maths.maxminRandom(12, 4),
                    z0: Maths.maxminRandom(4, 2),
                    y1: Maths.maxminRandom(12, 4),
                    x1: Maths.maxminRandom(12, 4),
                    z1: Maths.maxminRandom(4, 2),
                    yMod: Math.random() > .5 ? 1 : -1,
                    xMod: Math.random() > .5 ? -1 : 1,
                    zMod: Math.random() > .5 ? -1 : 1,
                }
            }
        }
    }

    drawHook() {
        this.move();
    }

    show() {
        if (this.isShow) return;

        this.isShow = true;
        this.isReset = false;
        this.image.visible = true;
        this.image.play();
    }

    hide() {
        if (!this.isShow) return;

        this.reset();
        this.isShow = false;
        this.image.pause();
        this.image.visible = false;
    }

    move() {
        if (this.isBlocked) return;

        const generalRotation = Maths.lerp(15, 30, this.progress) * -1;

        if (generalRotation < - 100 || generalRotation > 15) {
            this.hide();
            return;
        } else {
            this.show();
        }

        const scale = CircleSlider.isModeSuperman ? 1 : Maths.lerp(1, .7, CircleSlider.easing(Math.abs(this.progress) * .5));
        const center = Maths.lerp(0.3, .7, CircleSlider.easing(this.progressInside * .3));
        const rY = Maths.lerp(this.offset.rotation.y0 * this.offset.rotation.yMod, this.offset.rotation.y1 * this.offset.rotation.yMod * -1, this.progress);
        const rX = Maths.lerp(this.offset.rotation.x0 * this.offset.rotation.xMod, this.offset.rotation.x1 * this.offset.rotation.xMod * -1, this.progress);
        const rZ = Maths.lerp(this.offset.rotation.z0 * this.offset.rotation.zMod, this.offset.rotation.z1 * this.offset.rotation.zMod * -1, this.progress);
        const z = Maths.lerp(400, 500, this.progress)

        if (this.image) {
            this.image.material.uniforms.imageScale.value = scale;
            this.image.material.uniforms.scaleCenter.value = center;

            this.group.rotation.z = Functions.degreesToRadians(generalRotation);
            this.group.position.y = Metrics.HEIGHT * this.offsetPositionY + (this.offset.y * this.modAspectRatio) * this.progress;
            this.group.position.x = -Metrics.WIDTH * .4 + (this.offset.x * this.modAspectRatio) * this.progress + Math.abs(CircleSlider.getInstance().scroller.speed) * this.modSpeed;
            this.image.position.z = this.progress > .5 ? z + 100 * (this.progress - .5) : z;

            this.image.rotation.x = Functions.degreesToRadians(rX);
            this.image.rotation.y = Functions.degreesToRadians(rY);
            this.image.rotation.z = Functions.degreesToRadians(rZ);
        }
    }

    dispose() {
        super.dispose();
    }

    resize() {
        super.resize();



        if (this.image) {
            if (Metrics.WIDTH < Sizes.SMARTPHONE) {
                this.modSpeed = .8;
            } else {
                this.modSpeed = 2.859;
            }

            this.image.resize();

            const ratio = Metrics.WIDTH / Metrics.HEIGHT;
            const W = ratio < 1.6 ? Metrics.WIDTH * 1.1 : Metrics.WIDTH;


            this.modAspectRatio = 0.5 + (ratio - 1.0) * ((1.0 - 0.5) / (1.6 - 1.0));
            this.modAspectRatio = Math.max(0.5, ratio * .5);

            const initPos = this.modAspectRatio < .8? .55 : .65
            this.offsetPositionY = initPos + (this.modAspectRatio - .5) * .25;

            this.group.position.x = -W * .4;
            this.image.position.x = W * .7;
            this.group.position.y = Metrics.HEIGHT * (.5 + this.modAspectRatio);
        }
    }
}